import React, { useMemo } from 'react';
import HBText, { TSemanticTextColorKeys } from '../../../HBText/HBText';
import { IntentType, KanbanRegularColumn } from '../../HBKanban.types';
import { kanbanGroupThemeColors } from '../../HBKanban.constants';
import { useHBKanbanContext } from '../../HBKanbanContext';
import { vars } from '../../../../theme';
import {
    StyledColumnHeaderContainer,
    StyledColumnTitle,
    StyledItemCount,
    StyledColumnTitleText,
    StyledIconWrapper,
    StyledTitleWithIcon
} from './ColumnHeader.styles';

const getIntentTextColor = (intent?: IntentType): TSemanticTextColorKeys => {
    switch (intent) {
        case 'success':
            return 'text.success';
        case 'warning':
            return 'text.warning';
        case 'danger':
            return 'text.danger';
        case 'upgrade':
            return 'text.upgrade';
        case 'subtle':
            return 'text.tertiary';
        default:
            return 'text.primary';
    }
};

const getIntentColorValue = (intent?: IntentType): string => {
    switch (intent) {
        case 'success':
            return vars.colors.text.success;
        case 'warning':
            return vars.colors.text.warning;
        case 'danger':
            return vars.colors.text.danger;
        case 'upgrade':
            return vars.colors.text.upgrade;
        case 'subtle':
            return vars.colors.text.tertiary;
        default:
            return vars.colors.text.primary;
    }
};

const ColumnHeader = <TColumnData = any,>({
    column,
    headerRef
}: {
    column: KanbanRegularColumn<TColumnData>;
    headerRef?: React.RefObject<HTMLDivElement>;
}): JSX.Element => {
    const { renderProps, items, columnCounts, infiniteScrollProps, groups } =
        useHBKanbanContext<any, TColumnData>();
    const columnItems = items[column.id] || [];

    const groupColor = useMemo(() => {
        if (!column.groupId || !groups) {
            return undefined;
        }
        const group = groups.find(g => g.id === column.groupId);
        return group
            ? kanbanGroupThemeColors[group.theme].pipeColor
            : undefined;
    }, [column.groupId, groups]);

    const titleContent = renderProps.renderColumnHeaderTitle
        ? renderProps.renderColumnHeaderTitle(column)
        : column.title;

    // Use columnCounts from context (includes paginated items), fallback to visible items length
    const count = infiniteScrollProps
        ? columnCounts[column.id]
        : columnItems.length;
    const intent = column.intent;
    const textColor = getIntentTextColor(intent);
    const iconColor = getIntentColorValue(intent);

    const Icon = column.icon;

    return (
        <StyledColumnHeaderContainer ref={headerRef}>
            <StyledColumnTitle>
                <StyledTitleWithIcon $groupColor={groupColor}>
                    {Icon && (
                        <StyledIconWrapper>
                            <Icon color={iconColor} />
                        </StyledIconWrapper>
                    )}
                    <StyledColumnTitleText>
                        <HBText type="emphasis18" tag="h2" color={textColor}>
                            {titleContent}
                        </HBText>
                    </StyledColumnTitleText>
                </StyledTitleWithIcon>
                <StyledItemCount>
                    <HBText type="body14" color="text.secondary">
                        {count}
                    </HBText>
                </StyledItemCount>
            </StyledColumnTitle>
            {renderProps.renderColumnHeaderSuffix?.(column)}
        </StyledColumnHeaderContainer>
    );
};

export default ColumnHeader;
